import React, { useMemo, useState } from 'react';
import { useAsync, useInterval } from 'react-use-v13';
import apiFetch from 'react-scale/lib/api-fetch';
import { Alert, Chip, Dialog, DialogContent, Switch } from 'design-system';
import moment from 'moment';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import { ReactJsonView } from 'corp/components/ReactJsonView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import _ from 'lodash';
import { matching } from 'corp/components/SfnProgressBar/formPicture';
interface SfnEventBase {
  id: number;
  previousEventId?: number;
  type: string;
  timestamp: string;
}

interface TaskStateEnteredSfnEvent extends SfnEventBase {
  type: 'TaskStateEntered';
  stateEnteredEventDetails: {
    input: string;
    inputDetails: { truncated: boolean };
    name: string;
  };
}
interface TaskStateExitedSfnEvent extends SfnEventBase {
  type: 'TaskStateExited';
  stateExitedEventDetails: {
    output: string;
    outputDetails: { truncated: boolean };
    name: string;
  };
}
interface ActivityScheduledSfnEvent extends SfnEventBase {
  type: 'ActivityScheduled';
  activityScheduledEventDetails: {
    input: string;
    inputDetails: { truncated: boolean };
    resource: string;
    name: string;
  };
}
interface ActivitySucceededSfnEvent extends SfnEventBase {
  type: 'ActivitySucceeded';
  activitySucceededEventDetails: {
    output: string;
    outputDetails: { truncated: boolean };
  };
}
interface ActivityFailedSfnEvent extends SfnEventBase {
  type: 'ActivityFailed';
  activityFailedEventDetails: {
    error: string;
  };
}
interface ActivityStartedSfnEvent extends SfnEventBase {
  type: 'ActivityStarted';
  activityStartedEventDetails: {};
}

interface ExecutionStartedSfnEvent extends SfnEventBase {
  type: 'ExecutionStarted';
  executionStartedEventDetails: {
    input: string;
    inputDetails: { truncated: boolean };
    roleArn: string;
  };
}

interface ExecutionSucceededSfnEvent extends SfnEventBase {
  type: 'ExecutionSucceeded';
  executionSuceededEventDetails: {
    output: string;
    outputDetails: { truncated: boolean };
  };
}

interface ExecutionFailedSfnEvent extends SfnEventBase {
  type: 'ExecutionFailed';
  executionFailedEventDetails: {
    error: string;
  };
}

export type SfnEvent =
  | TaskStateEnteredSfnEvent
  | TaskStateExitedSfnEvent
  | ActivityScheduledSfnEvent
  | ActivityStartedSfnEvent
  | ActivitySucceededSfnEvent
  | ActivityFailedSfnEvent
  | ExecutionStartedSfnEvent
  | ExecutionSucceededSfnEvent
  | ExecutionFailedSfnEvent;

enum SfnItemStatus {
  Scheduled = 'scheduled',
  Started = 'started',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

enum SfnExecutionStatus {
  Started = 'started',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Pending = 'pending',
}

interface SfnItem {
  status: SfnItemStatus;
  description: string;
  pointer: number;
  error?: string;
  retryCount: number;
  history: {
    status: SfnItemStatus;
    timestamp: string;
  }[];
}

interface SfnGroup {
  name: string;
  items: SfnItem[];
}

const niceDisplayName = (arn: string) => {
  return arn.split(':').slice(-1)[0];
};

function jsonListToCsv<T>(jsonList: T[]): string {
  const keys: (keyof T)[] = _.keys(jsonList[0]) as any;
  const HEADER_ROW = keys;
  const table = [HEADER_ROW, ..._.map(jsonList, json => keys.map(k => json[k]))];
  return table.map(row => row.join(',')).join('\n');
}

export class SfnParser {
  groups: Record<string, SfnGroup> = {};
  pointers: Record<number, SfnItem> = {};
  log: SfnEvent[] = [];
  status = SfnExecutionStatus.Pending;
  startTime: Date | null = null;
  endTime: Date | null = null;
  arn: string;
  error?: string;
  totalSteps: number = 0;
  currentStep: number = 0;

  constructor(arn: string) {
    this.arn = arn;
  }

  isDone() {
    return !!this.endTime;
  }

  accept(event: SfnEvent) {
    let group: SfnGroup, payload: any;

    this.log.push(event);

    const previousItem: SfnItem | undefined = event.previousEventId
      ? this.pointers[event.previousEventId]
      : undefined;
    switch (event.type) {
      case 'ActivityScheduled':
        if (previousItem?.status === SfnItemStatus.Failed) {
          // A retry
          return this.updateItemForEvent(previousItem, SfnItemStatus.Scheduled, event);
        }
        // First time an event has shown up, make an entry for it
        group = this.getOrCreateGroup(event.activityScheduledEventDetails.resource);
        payload = JSON.parse(event.activityScheduledEventDetails.input).payload;
        this.scheduleActivityForGroup(group.name, event.id, event.timestamp, payload);
        break;
      case 'ActivityStarted':
        if (previousItem) {
          this.updateItemForEvent(previousItem, SfnItemStatus.Started, event);
        }
        break;
      case 'ActivitySucceeded':
        if (previousItem) {
          this.updateItemForEvent(previousItem, SfnItemStatus.Succeeded, event);
        }
        break;
      case 'ActivityFailed':
        if (previousItem) {
          this.updateItemForEvent(previousItem, SfnItemStatus.Failed, event);
        }
        break;
      case 'ExecutionStarted':
        this.status = SfnExecutionStatus.Started;
        this.startTime = new Date(event.timestamp);
        break;
      case 'ExecutionSucceeded':
        this.status = SfnExecutionStatus.Succeeded;
        this.endTime = new Date(event.timestamp);
        break;
      case 'ExecutionFailed':
        this.status = SfnExecutionStatus.Failed;
        this.endTime = new Date(event.timestamp);
        this.error = event.executionFailedEventDetails.error;
        break;
      case 'TaskStateEntered':
        break;
      case 'TaskStateExited':
        break;
      default:
        // There are other events, such Task- and Map- specific ones, but we'll ignore those for now.
        break;
    }
  }

  scheduleActivityForGroup(
    groupName: string,
    pointer: number,
    timestamp: string,
    activityPayload: any,
  ) {
    const group = this.groups[groupName];
    const item: SfnItem = {
      retryCount: 0,
      status: SfnItemStatus.Scheduled,
      pointer,
      description: JSON.stringify(activityPayload),
      history: [{ status: SfnItemStatus.Scheduled, timestamp }],
    };
    group.items.push(item);
    this.pointers[pointer] = item;
  }

  getOrCreateGroup(resource: string) {
    if (!this.groups[resource])
      this.groups[resource] = {
        name: resource,
        items: [],
      };
    return this.groups[resource];
  }

  updateItemForEvent(item: SfnItem, status: SfnItemStatus, event: SfnEvent) {
    item.history.push({
      status,
      timestamp: event.timestamp,
    });
    item.pointer = event.id;
    if (event.type === 'ActivityFailed') item.error = event.activityFailedEventDetails.error;
    if (status === SfnItemStatus.Scheduled && item.status === SfnItemStatus.Failed) {
      item.retryCount++;
      delete item.error;
    }
    item.status = status;
    this.pointers[event.id] = item;
  }

  /* Rendering things */

  describeJob() {
    if (this.status === SfnExecutionStatus.Pending) {
      return (
        <Alert severity="info">
          Waiting for job to start{' '}
          <IsomorphicLink target="_blank" href={'/lookup/' + this.arn}>
            More info
          </IsomorphicLink>
        </Alert>
      );
    }
    if (this.status === SfnExecutionStatus.Started && this.startTime) {
      return (
        <Alert severity="info">
          Job in progress for <TimeTickerSince from={this.startTime} />{' '}
          <IsomorphicLink target="_blank" href={'/lookup/' + this.arn}>
            More info
          </IsomorphicLink>
        </Alert>
      );
    }
    if (this.status === SfnExecutionStatus.Succeeded && this.startTime && this.endTime) {
      const start = moment(this.startTime);
      const end = moment(this.endTime);
      const diff = end.diff(start);
      const durationFormatted = moment.utc(diff).format('HH:mm:ss');
      return (
        <Alert severity="success">
          Job succeeded in {durationFormatted}{' '}
          <IsomorphicLink target="_blank" href={'/lookup/' + this.arn}>
            More info
          </IsomorphicLink>{' '}
        </Alert>
      );
    }
    if (this.status === SfnExecutionStatus.Failed && this.startTime && this.endTime) {
      const start = moment(this.startTime);
      const end = moment(this.endTime);
      const diff = end.diff(start);
      const durationFormatted = moment.utc(diff).format('HH:mm:ss');
      return (
        <Alert severity="error">
          Job failed in {durationFormatted} because: <strong>{this.error}</strong>{' '}
          <IsomorphicLink target="_blank" href={'/lookup/' + this.arn}>
            More info{' '}
          </IsomorphicLink>{' '}
        </Alert>
      );
    }
    return <div />;
  }

  calculateTotalSteps() {
    this.totalSteps = 0;
    for (const group of Object.values(this.groups)) {
      this.totalSteps += group.items.length;
    }
  }

  calculateCurrentStep() {
    this.currentStep = 0;
    for (const group of Object.values(this.groups)) {
      for (const item of group.items) {
        if (item.status === SfnItemStatus.Scheduled || item.status === SfnItemStatus.Started) {
          return;
        }
        this.currentStep++;
      }
    }
  }
}

const SfnActivityGroupHeader: React.FC<{
  group: SfnGroup;
  alexMode: boolean;
  setAlexMode: (alexMode: boolean) => void;
}> = ({ group, alexMode, setAlexMode }) => {
  const [status, statuses] = useMemo(() => {
    const statuses = new Set(group.items.map(item => item.status));

    const statusPriorityList = [
      SfnItemStatus.Failed,
      SfnItemStatus.Started,
      SfnItemStatus.Scheduled,
      SfnItemStatus.Succeeded,
    ];
    let status: SfnItemStatus = group.items[0].status;
    for (const possibleStatus of statusPriorityList) {
      if (statuses.has(possibleStatus)) {
        status = possibleStatus;
        break;
      }
    }
    return [status, statuses];
  }, [group]);

  const itemCount =
    group.items.length > 1 ? (
      <span style={{ margin: '0 10px' }}>
        <Chip size="small" label={group.items.length + ' jobs'} />
      </span>
    ) : null;

  const downloadAsCsv =
    group.items.length <= 1 ? (
      ''
    ) : (
      <a style={{ marginLeft: '5px' }} onClick={() => downloadCsvFn()}>
        {' '}
        <FontAwesomeIcon icon="download" />
        <span style={{ marginLeft: '5px' }}>CSV</span>
      </a>
    );

  const alexModeToggle = group.items.length > 1 && (
    <span style={{ marginLeft: '10px', display: 'inline-flex' }}>
      <div style={{ margin: '0 20px 0 10px' }}>Alex Mode </div>
      <Switch checked={alexMode} onChange={() => setAlexMode(!alexMode)} disabled={false} />
    </span>
  );

  const downloadCsvFn = () => {
    const data = group.items.map(({ description, status, retryCount, error }) => ({
      status,
      retryCount,
      error,
      description: JSON.stringify(_.omit(JSON.parse(description), '__sfMapIndex')),
    }));
    const url = window.URL.createObjectURL(new Blob([jsonListToCsv(data)]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `data.csv`);
    document.body.appendChild(link);
    link.click();
  };

  switch (status) {
    case SfnItemStatus.Scheduled:
      return (
        <Alert severity="info">
          <strong>{niceDisplayName(group.name)}</strong> {itemCount}
          Pending...
          {downloadAsCsv}
          {alexModeToggle}
        </Alert>
      );
    case SfnItemStatus.Started:
      return (
        <Alert severity="info">
          <strong>{niceDisplayName(group.name)}</strong>
          {itemCount}
          Running...
          {downloadAsCsv}
          {alexModeToggle}
        </Alert>
      );
    case SfnItemStatus.Succeeded:
      return (
        <Alert severity="success">
          <strong>{niceDisplayName(group.name)}</strong> {itemCount}
          Succeeded!
          {downloadAsCsv}
          {alexModeToggle}
        </Alert>
      );
    case SfnItemStatus.Failed:
      return (
        <Alert
          severity={
            statuses.has(SfnItemStatus.Scheduled) || statuses.has(SfnItemStatus.Started)
              ? 'warning'
              : 'error'
          }
        >
          <strong>{niceDisplayName(group.name)}</strong> {itemCount}
          {group.items.filter(item => item.status === SfnItemStatus.Failed).length} out of{' '}
          {group.items.length} Failed
          {downloadAsCsv}
          {alexModeToggle}
        </Alert>
      );
  }
};

const AlexMode: React.FC<{ group: SfnGroup }> = ({ group }) => {
  const sorted: SfnItem[][] = useMemo(
    () =>
      matching(
        group.items,
        (x: SfnItem) => x.status !== SfnItemStatus.Succeeded,
      ) as unknown as SfnItem[][],
    [group.items],
  );

  return (
    <div className={'alex-group'}>
      <style jsx>{`
        .alex-group__row {
          display: flex;
        }
      `}</style>
      {sorted.map((row, i) => (
        <div key={i} className={'alex-group__row'}>
          {row.map(item => (
            <SfnActivityItem item={item} key={item.pointer} />
          ))}
        </div>
      ))}
    </div>
  );
};

const SfnActivityGroup: React.FC<{ group: SfnGroup; alexMode?: boolean }> = ({ group }) => {
  const [alexMode, setAlexMode] = useState(false);
  const grouping = alexMode ? (
    <AlexMode group={group} />
  ) : (
    <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '10px' }}>
      {group.items.map(item => (
        <SfnActivityItem item={item} key={item.pointer} />
      ))}
    </div>
  );

  // Many sub items, so we break them out individually
  return (
    <div className="sfn-activity-group">
      <style jsx>{`
        .sfn-activity-group {
          border-bottom: 1px solid #ccc;
          padding: 10px;
        }
      `}</style>
      <div>
        <SfnActivityGroupHeader group={group} alexMode={alexMode} setAlexMode={setAlexMode} />
        <div>{group.items.length > 1 && grouping}</div>
      </div>
    </div>
  );
};

const SfnActivityItem: React.FC<{ item: SfnItem }> = ({ item }) => {
  const [showInfo, setShowInfo] = useState(false);
  const classByStatus = {
    [SfnItemStatus.Started]: 'sfn-activity-item--started',
    [SfnItemStatus.Scheduled]: 'sfn-activity-item--scheduled',
    [SfnItemStatus.Failed]: 'sfn-activity-item--failed',
    [SfnItemStatus.Succeeded]: 'sfn-activity-item--succeeded',
  };
  return (
    <div
      className={
        'sfn-activity-item ' +
        classByStatus[item.status] +
        (item.retryCount ? ' sfn-activity-item--retried' : '')
      }
      onClick={() => setShowInfo(true)}
    >
      <style jsx>{`
        .sfn-activity-item {
          width: 16px;
          height: 16px;
          border: 1px solid black;
          cursor: pointer;
          box-sizing: border-box;
        }

        .sfn-activity-item:hover {
          opacity: 0.8;
        }

        .sfn-activity-item--retried {
          border: 2px solid yellow;
        }
        .sfn-activity-item--scheduled {
          background: white;
        }
        .sfn-activity-item--started {
          background: var(--color-RemoBlue);
        }
        .sfn-activity-item--succeeded {
          background: var(--color-RemoGreen);
        }
        .sfn-activity-item--failed {
          background: var(--color-RemoRed);
        }
      `}</style>
      {showInfo && (
        <Dialog open={showInfo} onClose={() => setShowInfo(false)}>
          <DialogContent>
            {item.error && <Alert severity={'error'}>{item.error}</Alert>}
            {item.retryCount && (
              <Alert severity={'warning'}>
                This job was retried {item.retryCount} time(s) due to failures.{' '}
              </Alert>
            )}
            {item.description && (
              <ReactJsonView
                displayObjectSize={false}
                displayDataTypes={false}
                src={JSON.parse(item.description)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export interface SfnData {
  details: {
    executionHistory: {
      events: SfnEvent[];
    };
  };
}

const SfnProgressBar: React.FC<{ arn: string; onSuccess?: () => void; onFailure?: () => void }> = ({
  arn,
  onSuccess,
  onFailure,
}) => {
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [lastData, setLastData] = useState<SfnData | null>(null);
  const { loading, error } = useAsync(async () => {
    const data = await apiFetch<{
      details: {
        executionHistory: {
          events: SfnEvent[];
        };
      };
    }>(null, `corp-api/progress-bar/sfn-status`, {
      method: 'POST',
      body: {
        arn,
      },
    });

    if (!data || !data.details) return data;

    setLastData(data);
    return data;
  }, [arn, forceRefreshCounter]);

  const parser = useMemo(() => {
    if (!lastData) return null;
    const parser = new SfnParser(arn);
    for (const event of lastData.details.executionHistory.events) {
      parser.accept(event);
      if (event.type === 'ExecutionSucceeded') {
        onSuccess && onSuccess();
      }
      if (event.type === 'ExecutionFailed') {
        onFailure && onFailure();
      }
    }
    return parser;
  }, [lastData, arn, onSuccess, onFailure]);

  // We can refresh smaller payloads more frequently.
  const refreshTimeHeuristic = !parser ? null : Math.ceil(parser.log.length / 1000) * 3000;

  useInterval(
    () => {
      if (parser?.isDone()) return;
      if (document.hidden) return;
      if (loading || error) return;
      setForceRefreshCounter(x => x + 1);
    },
    parser?.isDone() ? null : refreshTimeHeuristic,
  );

  if (loading && !lastData) return <Alert severity="info">Loading job status...</Alert>;
  if (error) return <Alert severity="error">Unable to load job status</Alert>;

  /* okay, time to chunk the events! */
  return (
    <div>
      <style>{`
       .wrapper {
        border: 1px solid #ccc;
        border-radius: 5px;
        box-shadow: 3px 3px 3px #eee;
       }
    `}</style>
      {lastData && parser && (
        <div className="wrapper">
          <div className="header">{parser.describeJob()}</div>
          <div style={{ borderLeft: '5px solid #ccc' }}>
            {Object.values(parser.groups).map(group => (
              <SfnActivityGroup group={group} key={group.name} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TimeTickerSince: React.FC<{ from: Date }> = ({ from }) => {
  const [now, setNow] = useState(new Date());
  useInterval(() => setNow(new Date()), 1000);
  const durationFormmated = useMemo(() => {
    const diff = moment(now).diff(moment(from));
    return moment.utc(diff).format('HH:mm:ss');
  }, [now, from]);
  return <span>{durationFormmated}</span>;
};

export default SfnProgressBar;
